import { useMemberId } from '@trello/authentication';
import { isAdminOfEnterprise } from '@trello/business-logic/member';
import {
  isAdminOfOrganization,
  isMemberOfOrganization,
} from '@trello/business-logic/organization';
import { useWorkspaceId } from '@trello/id-context';
import { RouteId, useIsActiveRoute } from '@trello/router';
import {
  getOrganizationAccountUrl,
  getOrganizationExportUrl,
  getOrganizationMembersUrl,
  getOrganizationPowerUpsUrl,
  getOrganizationUrl,
} from '@trello/urls';

import { useAdminSettingsLinks } from 'app/src/components/AdminSettingsDropdown/useAdminSettingsLinks';
import { useWorkspaceSettingsNavigationMemberFragment } from './WorkspaceSettingsNavigationMemberFragment.generated';
import { useWorkspaceSettingsNavigationOrgFragment } from './WorkspaceSettingsNavigationOrgFragment.generated';

interface WorkspaceNavigationLink {
  href: string;
  isActive: boolean;
  isVisible: boolean;
}

interface WorkspaceNavigationLinks {
  boards: WorkspaceNavigationLink;
  members: WorkspaceNavigationLink;
  settings: WorkspaceNavigationLink;
  powerUps: WorkspaceNavigationLink;
  billing: WorkspaceNavigationLink;
  billingIssue: WorkspaceNavigationLink;
  upgrade: WorkspaceNavigationLink;
  export: WorkspaceNavigationLink;
}

export const useWorkspaceSettingsNavigationLinks =
  (): WorkspaceNavigationLinks => {
    const memberId = useMemberId();
    const { data: member } = useWorkspaceSettingsNavigationMemberFragment({
      from: { id: memberId },
    });

    const workspaceId = useWorkspaceId();
    const { data: organization } = useWorkspaceSettingsNavigationOrgFragment({
      from: { id: workspaceId },
    });

    const isWorkspaceAdmin =
      member && organization
        ? isAdminOfOrganization(member, organization, organization?.enterprise)
        : false;
    const isWorkspaceMember =
      member && organization
        ? isMemberOfOrganization(member, organization)
        : false;
    const isEnterpriseAdmin =
      member && organization?.idEnterprise
        ? isAdminOfEnterprise(member, organization?.idEnterprise)
        : false;

    const {
      shouldShowBilling,
      billingUrl,
      workspaceName,
      accountProblem,
      shouldShowUpgrade,
      shouldShowPowerups,
    } = useAdminSettingsLinks({
      workspaceId: workspaceId!,
      isWorkspaceAdmin,
      organization,
    });

    const shouldShowBoards =
      isWorkspaceMember ||
      isEnterpriseAdmin ||
      organization?.prefs?.permissionLevel === 'public';
    const shouldShowMembers = isWorkspaceMember || isEnterpriseAdmin;
    const shouldShowSettings = shouldShowMembers;
    const shouldShowExports = Boolean(
      (isWorkspaceAdmin || isEnterpriseAdmin) &&
        organization?.premiumFeatures?.includes('export'),
    );

    const isBoardsPage = useIsActiveRoute(
      new Set([RouteId.ORGANIZATION_BOARDS, RouteId.ORGANIZATION_BY_ID]),
    );
    const isMembersPage = useIsActiveRoute(
      new Set([
        RouteId.ORGANIZATION_MEMBERS,
        RouteId.ORGANIZATION_GUESTS,
        RouteId.ORGANIZATION_REQUESTS,
      ]),
    );
    const isSettingsPage = useIsActiveRoute(RouteId.ACCOUNT);
    const isPowerUpsPage = useIsActiveRoute(RouteId.ORGANIZATION_POWER_UPS);
    const isBillingPage = useIsActiveRoute(RouteId.WORKSPACE_BILLING);
    const isExportPage = useIsActiveRoute(RouteId.ORGANIZATION_EXPORT);

    return {
      boards: {
        href: getOrganizationUrl(workspaceName),
        isActive: isBoardsPage,
        isVisible: shouldShowBoards,
      },
      members: {
        href: getOrganizationMembersUrl(workspaceName),
        isActive: isMembersPage,
        isVisible: shouldShowMembers,
      },
      settings: {
        href: getOrganizationAccountUrl(workspaceName),
        isActive: isSettingsPage,
        isVisible: shouldShowSettings,
      },
      powerUps: {
        href: getOrganizationPowerUpsUrl(workspaceName),
        isActive: isPowerUpsPage,
        isVisible: shouldShowPowerups,
      },
      billing: {
        href: billingUrl,
        isActive: isBillingPage,
        isVisible: shouldShowBilling && !accountProblem,
      },
      billingIssue: {
        href: billingUrl,
        isActive: isBillingPage,
        isVisible: shouldShowBilling && accountProblem,
      },
      upgrade: {
        href: billingUrl,
        isActive: isBillingPage,
        isVisible: shouldShowUpgrade,
      },
      export: {
        href: getOrganizationExportUrl(workspaceName),
        isActive: isExportPage,
        isVisible: shouldShowExports,
      },
    };
  };
